import { Model, Attr, HasOne, PersistedSpraypaintRecord } from 'spraypaint';
import { NullProxy, RecordProxy } from 'spraypaint/lib-esm/proxies';
import BaseBusiness from './BaseBusiness';
import Business from './Business';

export type DraftBusinessProxy =
  | RecordProxy<DraftBusiness>
  | NullProxy<DraftBusiness>;

export type PersistedDraftBusiness = PersistedSpraypaintRecord<DraftBusiness>;

@Model()
export default class DraftBusiness extends BaseBusiness {
  static jsonapiType = 'draft-businesses';

  // --- Attributes
  // --------------

  @Attr() reviewStatus!: 'draft' | 'in_review' | 'rejected' | null;

  @Attr() rejectedMessage!: string;

  // --- Relationships
  // -----------------

  @HasOne() readonly business!: Business | null;

  // --- Paths
  // ---------

  /**
   * /backend/api/v1/draft-businesses/
   *
   * Method: get
   * Params:
   *   - include: string
   *   - fields: object
   *   - sort: string
   *   - page[number]: integer
   *   - page[size]: integer
   *   - sort: string
   *   - filter[search]: string
   *
   */

  /**
   * /backend/api/v1/draft-businesses/{id}/
   *
   * Method: get
   * Params:
   *   - id: string (required)
   *   - include: string
   *   - fields: object
   *   - sort: string
   *   - sort: string
   *   - filter[search]: string
   *
   * Method: patch
   * Params:
   *   - id: string (required)
   * Body:
   *   - name: { en: string; zh?: string; ja?: string; ms?: string }
   *   - excerpt: { en?: string; zh?: string; ja?: string; ms?: string }
   *   - description: { en?: string; zh?: string; ja?: string; ms?: string }
   *   - facebook: string
   *   - instagram: string
   *   - linked_in: string
   *   - website: string
   *   - phone_number: string
   *   - email: string (format: email) (maxLength: 254)
   *   - metadata: object
   *   - review_status: "draft" | "in_review" | "rejected"
   *   - rejected_message: string
   *
   */

  /**
   * /backend/api/v1/draft-businesses/{id}/cover-image/
   *
   * Method: post
   * Params:
   *   - id: string (required)
   * Body:
   *   - image: bytes
   *   - label: string
   *   - clone_from: Media
   *
   */

  /**
   * /backend/api/v1/draft-businesses/{id}/images/
   *
   * Method: post
   * Params:
   *   - id: string (required)
   * Body:
   *   - image: bytes
   *   - label: string
   *   - clone_from: Media
   *
   */

  /**
   * /backend/api/v1/draft-businesses/{id}/logo/
   *
   * Method: post
   * Params:
   *   - id: string (required)
   * Body:
   *   - image: bytes
   *   - label: string
   *   - clone_from: Media
   *
   */

  // --- Custom

  get isInReview() {
    return this.reviewStatus === 'in_review';
  }

  get isRejected() {
    return this.reviewStatus === 'rejected';
  }

  async mergeBusiness(): Promise<string[]> {
    const businessId = this.business?.id;
    if (!businessId) {
      throw new Error('No business');
    }
    const business = await Business.find(businessId).then(res => res.data);
    return super.mergeAndSaveAllDataFromSource(business);
  }

  getMergeKeysToIgnore(keys: string[]) {
    const _keys = super.getMergeKeysToIgnore(keys);
    return [
      ..._keys,
      'slug',
      'isPublic',
      'owner',
      'draftBusiness',
      //
    ];
  }
}
